/**
 * Notification Service
 * Handles fetching and managing user notifications and pending actions
 */

import io from 'socket.io-client';

class NotificationService {
    constructor() {
        this.pollInterval = null;
        this.listeners = [];
        this.isPolling = false;
        this.pollingIntervalMs = 30000; // Poll every 30 seconds
        this.socket = null;
        this.userUid = null;
    }

    /**
     * Initialize socket connection
     * @param {number} userUid - User's UID
     */
    initSocket(userUid) {
        if (this.socket && this.socket.connected) {
            console.log('[NotificationService] Socket already connected');
            return;
        }

        this.userUid = userUid;

        try {
            this.socket = io(window.devMode ? 'http://localhost:80' : window.location.origin, {
                transports: ['polling'], // Use polling only, avoid WebSocket upgrade issues
                reconnection: true,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 5000,
                reconnectionAttempts: Infinity
            });

            this.socket.on('connect', () => {
                console.log('[NotificationService] Socket connected');
                // Join user's personal room
                this.socket.emit('join_user_room', { uid: userUid });
            });

            this.socket.on('new_notification', (data) => {
                console.log('[NotificationService] Received real-time notification:', data);
                // Fetch latest notifications to update UI
                this.fetchNotifications();
            });

            this.socket.on('disconnect', () => {
                console.log('[NotificationService] Socket disconnected');
            });

            this.socket.on('error', (error) => {
                console.error('[NotificationService] Socket error:', error);
            });
        } catch (error) {
            console.error('[NotificationService] Error initializing socket:', error);
        }
    }

    /**
     * Disconnect socket
     */
    disconnectSocket() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
    }

    /**
     * Start polling for notifications and pending actions
     * @param {string} userEmail - User's email
     * @param {number} userUid - User's UID
     * @param {function} callback - Callback function to receive notifications and pending actions
     */
    startPolling(userEmail, userUid, callback) {
        if (this.isPolling) {
            console.log('[NotificationService] Already polling');
            return;
        }

        if (!userEmail) {
            console.error('[NotificationService] Cannot start polling without user email');
            return;
        }

        this.isPolling = true;
        this.userEmail = userEmail;

        // Initialize socket connection if userUid provided
        if (userUid) {
            this.initSocket(userUid);
        }

        // Add callback to listeners
        if (callback && !this.listeners.includes(callback)) {
            this.listeners.push(callback);
        }

        // Fetch immediately
        this.fetchNotifications();

        // Then poll at interval
        this.pollInterval = setInterval(() => {
            this.fetchNotifications();
        }, this.pollingIntervalMs);

        console.log('[NotificationService] Started polling for notifications');
    }

    /**
     * Stop polling for notifications
     */
    stopPolling() {
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
        this.disconnectSocket();
        this.isPolling = false;
        console.log('[NotificationService] Stopped polling for notifications');
    }

    /**
     * Add a listener for notifications
     * @param {function} callback - Callback function
     */
    addListener(callback) {
        if (callback && !this.listeners.includes(callback)) {
            this.listeners.push(callback);
        }
    }

    /**
     * Remove a listener
     * @param {function} callback - Callback function to remove
     */
    removeListener(callback) {
        this.listeners = this.listeners.filter(cb => cb !== callback);
    }

    /**
     * Fetch notifications and pending actions from server
     */
    async fetchNotifications() {
        if (!this.userEmail) {
            console.error('[NotificationService] No user email set');
            return;
        }

        try {
            const url = window.devMode ? 'http://localhost:80/get_notifications' : '/get_notifications';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: this.userEmail,
                    limit: 50,
                    offset: 0
                })
            });

            if (response.ok) {
                const result = await response.json();
                const notifications = result.notifications || [];

                // Fetch pending actions
                const pendingUrl = window.devMode ? 'http://localhost:80/get_pending_actions' : '/get_pending_actions';
                const pendingResponse = await fetch(pendingUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: this.userEmail })
                });

                let pendingActions = [];
                if (pendingResponse.ok) {
                    const pendingResult = await pendingResponse.json();
                    pendingActions = pendingResult.pending_actions || [];
                }

                // Notify all listeners
                this.listeners.forEach(callback => {
                    callback({
                        notifications,
                        pendingActions,
                        totalNotifications: result.total || 0,
                        totalPendingActions: pendingActions.length
                    });
                });
            } else {
                console.error('[NotificationService] Error fetching notifications:', response.status);
            }
        } catch (error) {
            console.error('[NotificationService] Network error fetching notifications:', error);
        }
    }

    /**
     * Mark a notification as read
     * @param {number} notificationDate - The date/timestamp of the notification
     */
    async markAsRead(notificationDate) {
        if (!this.userEmail) {
            console.error('[NotificationService] No user email set');
            return false;
        }

        try {
            const url = window.devMode ? 'http://localhost:80/mark_notification_read' : '/mark_notification_read';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: this.userEmail,
                    notification_date: notificationDate
                })
            });

            if (response.ok) {
                // Refresh notifications
                this.fetchNotifications();
                return true;
            } else {
                console.error('[NotificationService] Error marking notification as read');
                return false;
            }
        } catch (error) {
            console.error('[NotificationService] Network error marking notification as read:', error);
            return false;
        }
    }

    /**
     * Clear all notifications for the user
     */
    async clearAll() {
        if (!this.userEmail) {
            console.error('[NotificationService] No user email set');
            return false;
        }

        try {
            const url = window.devMode ? 'http://localhost:80/clear_all_notifications' : '/clear_all_notifications';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: this.userEmail })
            });

            if (response.ok) {
                // Refresh notifications
                this.fetchNotifications();
                return true;
            } else {
                console.error('[NotificationService] Error clearing notifications');
                return false;
            }
        } catch (error) {
            console.error('[NotificationService] Network error clearing notifications:', error);
            return false;
        }
    }

    /**
     * Set polling interval
     * @param {number} intervalMs - Interval in milliseconds
     */
    setPollingInterval(intervalMs) {
        this.pollingIntervalMs = intervalMs;
        
        // Restart polling if active
        if (this.isPolling) {
            this.stopPolling();
            this.startPolling(this.userEmail);
        }
    }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;
